/**
 * Daily Fortune API 模組
 *
 * 提供每日幸運元素相關的 API 功能：
 * - 取得今日幸運元素（數字、顏色、形狀、方位）
 * - 每日快取機制（一天只能查詢一次）
 *
 * @module api/dailyfortune
 */

import { apiClient } from './client';

/**
 * 每日幸運元素回應介面
 */
export interface DailyFortuneResponse {
  success: boolean;
  message?: string;
  query_id?: number;
  output?: string;
  result?: {
    lucky_numbers: number[];
    lucky_color_rgb: { R: number; G: number; B: number };
    lucky_color_hex: string;
    lucky_color_nearest: string;
    lucky_shape: string;
    lucky_direction: string;
  };
  cost?: number;
  balance_after?: number;
  cached?: boolean;
  date?: string;
  query_time?: string;
  chart_tip?: string;
  recommended_question_id?: string;
  recommended_question_text?: string;
}

/**
 * 取得今日幸運元素
 *
 * @returns 今日幸運元素
 *
 * @example
 * ```typescript
 * const fortune = await getDailyFortune();
 * console.log(fortune.result.lucky_numbers); // [7, 23]
 * console.log(fortune.result.lucky_color_hex); // "#FF5733"
 * ```
 */
export const getDailyFortune = async (): Promise<DailyFortuneResponse> => {
  const response = await apiClient.get<DailyFortuneResponse>('/api/daily-fortune');
  return response as any as DailyFortuneResponse;
};

/**
 * 取得顏色的顯示名稱（中文）
 *
 * @param colorName 英文顏色名稱
 * @returns 中文顏色名稱
 */
export const getColorNameChinese = (colorName: string): string => {
  const colorMap: Record<string, string> = {
    'white': '純白色',
    'ivory': '象牙白',
    'silver': '銀色',
    'charcoal': '炭灰色',
    'black': '純黑色',
    'light blue': '淺藍色',
    'sky blue': '天空藍',
    'blue': '藍色',
    'dark blue': '深藍色',
    'navy': '藏青色',
    'indigo': '靛藍色',
    'lavender': '薰衣草紫',
    'purple': '紫色',
    'magenta': '洋紅色',
    'rose': '玫瑰色',
    'pink': '粉紅色',
    'crimson': '純紅色',
    'brick red': '磚紅色',
    'dark red': '深紅色',
    'coral': '珊瑚色',
    'orange': '橘色',
    'apricot': '杏色',
    'golden yellow': '金黃色',
    'bright yellow': '鮮黃色',
    'olive': '橄欖色',
    'lime green': '萊姆綠',
    'mint green': '薄荷綠',
    'green': '純綠色',
    'dark green': '深綠色',
    'brown': '咖啡色',
  };

  const normalized = colorName.toLowerCase();
  return colorMap[normalized] || colorName;
};

/**
 * 取得方位的 emoji 圖標
 *
 * @param direction 方位名稱
 * @returns emoji 圖標
 */
export const getDirectionEmoji = (direction: string): string => {
  const directionMap: Record<string, string> = {
    '東方': '🧭 ➡️',
    '東南方': '🧭 ↘️',
    '南方': '🧭 ⬇️',
    '西南方': '🧭 ↙️',
    '西方': '🧭 ⬅️',
    '西北方': '🧭 ↖️',
    '北方': '🧭 ⬆️',
    '東北方': '🧭 ↗️',
  };

  return directionMap[direction] || '🧭';
};

/**
 * 取得形狀的 emoji 圖標（簡化版）
 *
 * @param shape 形狀名稱
 * @returns emoji 圖標
 */
export const getShapeEmoji = (shape: string): string => {
  const shapeMap: Record<string, string> = {
    // 基礎幾何 (1-10)
    '圓形': '⭕',
    '橢圓形': '⭕',
    '正方形': '⬛',
    '長方形': '▬',
    '菱形': '♦️',
    '三角形': '🔺',
    '倒三角形': '🔻',
    '等腰三角形': '🔺',
    '直角三角形': '📐',
    '梯形': '🔺',
    // 多邊形 (11-20)
    '五邊形': '⬠',
    '六邊形': '⬡',
    '七邊形': '🔷',
    '八邊形': '🛑',
    '十邊形': '🔶',
    '五角星': '⭐',
    '六角星': '✡️',
    '八角星': '✴️',
    '十字形': '➕',
    '米字形': '✳️',
    // 自然形狀 (21-30)
    '心形': '❤️',
    '水滴形': '💧',
    '葉子形': '🍃',
    '花瓣形': '🌸',
    '雲朵形': '☁️',
    '月牙形': '🌙',
    '太陽形': '☀️',
    '火焰形': '🔥',
    '海浪形': '🌊',
    '山形': '⛰️',
    // 植物圖案 (31-40)
    '酢醬草形': '🌿',
    '四葉草形': '🍀',
    '楓葉形': '🍁',
    '銀杏葉形': '🍂',
    '梅花形': '🌸',
    '櫻花形': '🌸',
    '蓮花形': '🌺',
    '向日葵形': '🌻',
    '玫瑰花形': '🌹',
    '百合花形': '🌺',
    // 動物圖案 (41-50)
    '蝴蝶形': '🦋',
    '鳥形': '🐦',
    '魚形': '🐟',
    '貝殼形': '🐚',
    '螺旋形': '🌀',
    '羽毛形': '🪶',
    '翅膀形': '🕊️',
    '爪形': '🐾',
    '鱗片形': '🐠',
    '波紋形': '〰️',
    // 其他圖案 (51-60)
    '盾牌形': '🛡️',
    '皇冠形': '👑',
    '鑰匙形': '🔑',
    '錨形': '⚓',
    '弓形': '🏹',
    '箭頭形': '➡️',
    '閃電形': '⚡',
    '齒輪形': '⚙️',
    '結形': '🎀',
    '無限符號形': '♾️',
    // 舊版相容
    '方形': '▪️',
    '五芒星形': '⭐',
    '六角形': '🔷',
    '雲形': '☁️',
    '花形': '🌸',
    '葉形': '🍃',
    '鑽石形': '💎',
    '鈴鐺形': '🔔',
    '彩虹形': '🌈',
    '音符形': '🎵',
    '眼形': '👁️',
    '旗幟形': '🚩',
    '星形': '⭐',
  };
  return shapeMap[shape] || '◆';
};

/**
 * 格式化數字列表顯示
 *
 * @param numbers 數字陣列
 * @returns 格式化的字串
 */
export const formatLuckyNumbers = (numbers: number[]): string => {
  return numbers.sort((a, b) => a - b).join(', ');
};

/**
 * 檢查是否為暖色系
 *
 * @param rgb RGB 顏色值
 * @returns true 表示暖色系
 */
export const isWarmColor = (rgb: { R: number; G: number; B: number }): boolean => {
  // 簡單判斷：紅色和黃色成分較高為暖色
  return (rgb.R + rgb.G) > rgb.B * 1.5;
};

/**
 * 取得顏色的對比文字顏色（黑或白）
 *
 * @param hex Hex 顏色值
 * @returns 'black' 或 'white'
 */
export const getContrastTextColor = (hex: string): 'black' | 'white' => {
  // 移除 # 符號
  const cleanHex = hex.replace('#', '');

  // 轉換為 RGB
  const r = parseInt(cleanHex.substr(0, 2), 16);
  const g = parseInt(cleanHex.substr(2, 2), 16);
  const b = parseInt(cleanHex.substr(4, 2), 16);

  // 計算相對亮度
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // 亮度 > 0.5 使用黑字，否則白字
  return luminance > 0.5 ? 'black' : 'white';
};

/**
 * 取得顏色的 CSS gradient 背景
 *
 * @param hex Hex 顏色值
 * @returns CSS gradient 字串
 */
export const getColorGradient = (hex: string): string => {
  // 創建漸層效果（從淺到深）
  return `linear-gradient(135deg, ${hex}CC 0%, ${hex} 100%)`;
};
